export function readableNumber(
  number: number,
  presetOrOptions: readableNumber.Preset | readableNumber.Options = '#.',
): string {
  const {
    decimals = { max: 2 },
    noCommas = false,
    prefix = '',
    scalar = 1,
    suffix = '',
  } = readableNumber.Preset.isPreset(presetOrOptions)
    ? readableNumber.Preset.toOptions(presetOrOptions)
    : presetOrOptions

  // We multiply the number with a given scalar
  number *= scalar

  // We format the whole number and the decimals separately
  let [wholeNumber, decimal] = number.toString().split('.')
  if (!noCommas) {
    // For the whole number, we add commas in the right spots, unless `noCommas`
    // is true
    wholeNumber = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  // For the decimals, we first figure out the minimum and maximum allowed
  // decimals
  let minDecimals =
    (typeof decimals === 'number' ? decimals : decimals.min) ?? 0
  let maxDecimals =
    (typeof decimals === 'number' ? decimals : decimals.max) ?? Infinity
  if (isNaN(minDecimals) || minDecimals < 0) {
    // Minimum decimals cannot be negative or NaN. In both cases, we just treat
    // it like 0
    minDecimals = 0
  }
  // Maximum decimals also cannot be negative or NaN...
  if (isNaN(maxDecimals)) {
    // But we treat NaN as Infinity (i.e. no maximum)
    maxDecimals = Infinity
  } else if (maxDecimals < 0) {
    // And we treat negative as 0 (the smallest allowed maximum)
    maxDecimals = 0
  }
  if (maxDecimals < minDecimals) {
    // If we get an invalid range, we throw an error
    throw new Error(
      `Allowed decimal range is invalid. Maximum allowed decimals (${maxDecimals}) cannot be less than minimum allowed decimals (${minDecimals}).`,
    )
  }
  if (decimal === undefined) {
    // If there is no decimal...
    if (minDecimals === 0) {
      // And we allow no decimal, then we can just return the whole number
      return `${prefix}${wholeNumber}${suffix}`
    }
    // Otherwise, we need to create a string with the minimum allowed decimal
    // digits. We start with an empty string and the rest is added below
    decimal ??= ''
  }
  if (decimal.length < minDecimals) {
    // If the decimal is too small, we fill in with additional 0s to meet the
    // minimum allowed decimal digits
    decimal += '0'.repeat(minDecimals - decimal.length)
  } else if (decimal.length > maxDecimals) {
    // If the decimal is too large, we round the number properly to handle
    // carries into the whole part
    const factor = 10 ** maxDecimals
    const rounded = Math.round(number * factor) / factor
    ;[wholeNumber, decimal] = rounded.toString().split('.')
    if (!noCommas) {
      wholeNumber = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    decimal ??= ''
    if (decimal.length < minDecimals) {
      decimal += '0'.repeat(minDecimals - decimal.length)
    }
  }
  // Finally, we recombine the formatted whole number and decimal
  if (!decimal) {
    return `${prefix}${wholeNumber}${suffix}`
  }
  return `${prefix}${wholeNumber}.${decimal}${suffix}`
}
export namespace readableNumber {
  export interface Options {
    /**
     * Number is multiplied by this scalar before being formatted
     *
     * @default 1
     * @example
     * readableNumber(1, { scalar: 1 }) === '1'
     * readableNumber(0.5, { scalar: 1 }) === '0.5'
     * readableNumber(2, { scalar: 1 }) === '2'
     *
     * readableNumber(1, { scalar: 2 }) === '2'
     * readableNumber(0.5, { scalar: 2 }) === '1'
     * readableNumber(2, { scalar: 2 }) === '4'
     *
     * readableNumber(1, { scalar: 100 }) === '100'
     * readableNumber(0.5, { scalar: 100 }) === '50'
     * readableNumber(2, { scalar: 100 }) === '200'
     */
    scalar?: number
    prefix?: string
    suffix?: string
    /**
     * Set to `true` to avoid comma-separating numbers >= 1000
     *
     * @default false
     * @example
     * readableNumber(123456789) === '123,456,789'
     * readableNumber(123456789, { noCommas: true }) === '123456789'
     */
    noCommas?: boolean
    /**
     * Determines how many decimals are shown. Excessive decimals are rounded to
     * the nearest valid number
     *
     * @default { max: 2 }
     * @example
     * readableNumber(123, { decimals: 2 }) === '123.00'
     * readableNumber(123.4, { decimals: 2 }) === '123.40'
     * readableNumber(123.45, { decimals: 2 }) === '123.45'
     * readableNumber(123.456, { decimals: 2 }) === '123.46'
     *
     * readableNumber(123, { decimals: { min: 1, max: 3 } }) === '123.0'
     * readableNumber(123.4, { decimals: { min: 1, max: 3 } }) === '123.4'
     * readableNumber(123.45, { decimals: { min: 1, max: 3 } }) === '123.45'
     * readableNumber(123.456, { decimals: { min: 1, max: 3 } }) === '123.456'
     * readableNumber(123.4567, { decimals: { min: 1, max: 3 } }) === '123.457'
     */
    decimals?: number | { min?: number; max?: number }
  }
  export namespace Options {
    export function isOptions(val: unknown): val is Options {
      return (
        !!val &&
        typeof val === 'object' &&
        'scalar' in val &&
        typeof val.scalar === 'number' &&
        'prefix' in val &&
        typeof val.prefix === 'string' &&
        'suffix' in val &&
        typeof val.suffix === 'string' &&
        'noCommas' in val &&
        typeof val.noCommas === 'boolean'
      )
    }
  }

  /**
   * These represent standard {@link FormatNumber} of numbers
   */
  export type Preset = '$' | '%' | '#' | '#.'
  export namespace Preset {
    const LOOKUP: Record<Preset, Options> = {
      /** Integer */
      '#': {
        decimals: 0,
      },
      /** Decimal */
      '#.': {
        decimals: { max: 2 },
      },
      /** Dollar */
      $: {
        decimals: { max: 2, min: 2 },
        prefix: '$',
      },
      /** Percent */
      '%': {
        decimals: 0,
        scalar: 100,
        suffix: '%',
      },
    }

    export function isPreset(val: unknown): val is Preset {
      return typeof val === 'string' && Object.keys(LOOKUP).includes(val)
    }

    /**
     * Returns {@link Options} for the given {@link Preset}
     */
    export function toOptions(preset: Preset): Options {
      return LOOKUP[preset]
    }
  }
}
